import { Icon } from '@iconify/react';
import { Align, Popup as KPopup } from '@progress/kendo-react-popup';
import clsx from 'clsx';
import {
  type FC,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

export interface OptionsMenuItem {
  id: string;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}

export interface OptionsMenuProps {
  options: OptionsMenuItem[];
  ariaLabel?: string;
  trigger?: ReactNode;
  triggerClassName?: string;
  menuClassName?: string;
  anchorAlign?: Align;
  popupAlign?: Align;
  onOpenChange?: (open: boolean) => void;
}

const defaultTriggerClassName =
  'flex h-7 w-7 items-center justify-center rounded border border-[#D1D1D1] bg-[#F5F5F5] text-[#4A5565] hover:bg-gray-100';

const OptionsMenu: FC<OptionsMenuProps> = ({
  options,
  ariaLabel = 'More options',
  trigger,
  triggerClassName = defaultTriggerClassName,
  menuClassName = '',
  anchorAlign = { horizontal: 'right', vertical: 'bottom' },
  popupAlign = { horizontal: 'right', vertical: 'top' },
  onOpenChange,
}): ReactElement => {
  const menuId = useId();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  const setMenuOpen = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange]
  );

  const handleToggle = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setMenuOpen(!open);
    },
    [open, setMenuOpen]
  );

  const handleOptionClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>, option: OptionsMenuItem) => {
      event.stopPropagation();

      if (option.disabled) {
        return;
      }

      option.onClick?.();
      setMenuOpen(false);
    },
    [setMenuOpen]
  );

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const focusFrame = window.requestAnimationFrame(() => {
      popupRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus();
    });

    const handlePointerDown = (event: Event) => {
      const target = event.target as Node;

      if (anchorRef.current?.contains(target) || popupRef.current?.contains(target)) {
        return;
      }

      setMenuOpen(false);
    };

    const getMenuItems = () =>
      Array.from(
        popupRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ??
          []
      );

    const getFocusableAfterTrigger = (previous = false) => {
      const trigger = anchorRef.current;

      if (!trigger) {
        return null;
      }

      const focusableElements = Array.from(
        document.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !popupRef.current?.contains(element));

      const triggerIndex = focusableElements.indexOf(trigger);

      if (triggerIndex === -1) {
        return null;
      }

      return previous
        ? (focusableElements[triggerIndex - 1] ?? null)
        : (focusableElements[triggerIndex + 1] ?? null);
    };

    const handleKeyboardDismiss = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const menuItems = getMenuItems();
      const currentIndex = menuItems.findIndex((item) => item === document.activeElement);
      const isLastMenuItem = currentIndex === menuItems.length - 1;
      const isFirstMenuItem = currentIndex === 0;

      if (!event.shiftKey && isLastMenuItem && menuItems.length > 0) {
        event.preventDefault();
        const nextControl = getFocusableAfterTrigger();
        setMenuOpen(false);
        window.requestAnimationFrame(() => nextControl?.focus());
        return;
      }

      if (event.shiftKey && isFirstMenuItem && menuItems.length > 0) {
        event.preventDefault();
        setMenuOpen(false);
        window.requestAnimationFrame(() => anchorRef.current?.focus());
        return;
      }

      window.requestAnimationFrame(() => {
        const { activeElement } = document;

        if (
          !anchorRef.current?.contains(activeElement) &&
          !popupRef.current?.contains(activeElement)
        ) {
          setMenuOpen(false);
        }
      });
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyboardDismiss);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyboardDismiss);
    };
  }, [open, setMenuOpen]);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={triggerClassName}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={handleToggle}
      >
        {trigger ?? <Icon icon="lucide:ellipsis-vertical" height={14} width={14} />}
      </button>

      {open && (
        <KPopup
          anchor={anchorRef.current}
          show={open}
          animate={false}
          anchorAlign={anchorAlign}
          popupAlign={popupAlign}
          className="!p-0"
        >
          <div
            ref={popupRef}
            id={menuId}
            role="menu"
            aria-label={ariaLabel}
            className={clsx(
              'min-w-[140px] overflow-hidden rounded border border-[#D1D1D1] bg-white py-1 shadow-md',
              menuClassName
            )}
            onClick={(event) => event.stopPropagation()}
          >
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                role="menuitem"
                disabled={option.disabled}
                className={clsx(
                  'block w-full px-3 py-1.5 text-left text-sm font-semibold text-[#111111]',
                  'hover:bg-[#F5F5F5] focus:bg-[#F5F5F5] focus:outline-none',
                  option.disabled && 'cursor-not-allowed text-[#AAAAAA] hover:bg-white'
                )}
                onClick={(event) => handleOptionClick(event, option)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </KPopup>
      )}
    </>
  );
};

export default OptionsMenu;
